import { observer } from "mobx-react-lite"
import { Link } from "react-router-dom"
import { useAccount, useConfig } from "wagmi"

import { Card } from "@/components/card"
import { ShowWhenTrue } from "@/components/conditionals"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { claimLpFeesInteraction } from "@/domains/interactions"
import { MinimalPoolStore } from "@/domains/minimal-pool/minimal-pool-store"
import { NoFeesView } from "@/features/rewards/components/no-fees-view"
import { StoreDesktopFeesView } from "@/features/rewards/components/store-fees-desktop-view"
import { StoreMobileFeesView } from "@/features/rewards/components/store-fees-mobile-view"
import { StoreTabletFeesView } from "@/features/rewards/components/store-fees-tablet-view"
import { useChainConfig } from "@/hooks/use-chain-config"
import useResponsive from "@/hooks/use-responsive"
import useEventEmitter from "@/hooks/useEventEmitter"

export const FeeRewards = observer(() => {
  const account = useAccount()
  const chainConfig = useChainConfig()
  const config = useConfig()
  const emitter = useEventEmitter()

  const scaleApp = useScaleApplication()

  const { isMobile, isTablet, isDesktop } = useResponsive()

  const onClaimMinimalPoolFees = async (pool: MinimalPoolStore) => {
    try {
      if (!account.address) {
        throw new Error("no address found")
      }
      const result = await claimLpFeesInteraction({
        account: account.address,
        chainId: chainConfig.chainId,
        config,
        pairAddress: pool.address.toViemAddress(),
        pairDisplayName: pool.displayName ?? "unknown",
        emitter,
      })

      if (result.success) {
        scaleApp.MinimalPools.Orchestrator.userInfoPairsQueryInstance?.forceExecute()
      }
    } catch (error) {
      console.error("Rewards→onClaimLpFees error", error)
    }
  }

  const pools = scaleApp.MinimalPools.Repository.getAll()
  // do not memoize this, just filter on render
  const feePools = pools.filter(
    (v) => v.onChainUserBalance > 0n || v.onChainUserClaimableFees0 > 0n || v.onChainUserClaimableFees1 > 0n,
  )
  const hasFees = feePools.length > 0

  return (
    <Card.Container className="flex w-full flex-col gap-6 rounded-lg border border-p400-light bg-p400 p-4 lg:p-6">
      <div className="flex flex-row items-center justify-between gap-2">
        <div>
          <Card.HeaderTitle>Fees</Card.HeaderTitle>
          <Card.HeaderDescription>
            Fees are earned by holding{" "}
            <Link to="/liquidity" className="font-normal">
              liquidity positions
            </Link>{" "}
            in your wallet.
          </Card.HeaderDescription>
        </div>
      </div>

      <ShowWhenTrue when={hasFees && isMobile}>
        <StoreMobileFeesView pools={feePools} onClaimLpFees={onClaimMinimalPoolFees} />
      </ShowWhenTrue>

      <ShowWhenTrue when={hasFees && isTablet}>
        <StoreTabletFeesView pools={feePools} onClaimLpFees={onClaimMinimalPoolFees} />
      </ShowWhenTrue>

      <ShowWhenTrue when={hasFees && isDesktop}>
        <StoreDesktopFeesView pools={feePools} onClaimLpFees={onClaimMinimalPoolFees} />
      </ShowWhenTrue>

      {/* Show when no fees */}
      <ShowWhenTrue when={!hasFees}>
        <NoFeesView />
      </ShowWhenTrue>
    </Card.Container>
  )
})
